import { useEffect, useMemo, useState } from 'react';
import {
	getCountdownTarget,
	getTimeLeft,
	TimeLeft,
} from '../components/stickyBanner/stickyBanner.helper';
import {
	CountdownSettings,
	SiteTimezone,
} from '../components/stickyBanner/types';

interface BannerCountdown {
	/** Countdown end (epoch ms), or null when there is no valid end date. */
	target: number | null;
	timeLeft: TimeLeft;
}

/**
 * Live countdown for a sticky banner template. Ticks every second until
 * the end moment and then stops.
 *
 * @param countdown    Countdown settings.
 * @param siteTimezone Site timezone localized by PHP.
 */
export const useBannerCountdown = (
	countdown: CountdownSettings,
	siteTimezone: SiteTimezone
): BannerCountdown => {
	const [ now, setNow ] = useState( () => Date.now() );

	const target = useMemo(
		() => getCountdownTarget( countdown, siteTimezone ),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			countdown.endDate,
			countdown.endTime,
			countdown.timezone,
			siteTimezone.timezone,
			siteTimezone.gmtOffset,
		]
	);

	useEffect( () => {
		if ( target === null ) {
			return;
		}
		setNow( Date.now() );
		const interval = window.setInterval( () => {
			const current = Date.now();
			setNow( current );
			if ( current >= target ) {
				window.clearInterval( interval );
			}
		}, 1000 );
		return () => window.clearInterval( interval );
	}, [ target ] );

	return { target, timeLeft: getTimeLeft( target, now ) };
};
